import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

/**
 * **L'enveloppe d'un champ : libellé, aide, erreur — et le lien entre les trois.**
 *
 * C'est l'arbitrage `ponytail` que `design-system-first` écrit noir sur blanc :
 * « **encapsuler le natif, ne pas le remplacer** ». `Saisie` ci-dessous rend un `<input>`
 * ordinaire ; ce qui est factorisé est ce qu'on oublie — l'identifiant qui relie le
 * libellé au champ, `aria-describedby` vers l'aide, `aria-invalid` et le lien vers
 * l'erreur.
 *
 * **UN BORD N'EST TENU AU SEUIL QUE LÀ OÙ IL EST LE SEUL INDICE D'EXISTENCE** — la charte
 * réserve `--bord-champ`, tenu à 3:1, au **champ vide**. « Les séparateurs et les bords de
 * surface sont décoratifs. » C'est pour cela que la saisie porte `border-bord-champ` et
 * pas `border-bord`.
 */

type ProprietesDuChamp = {
  readonly libelle: string;
  readonly aide?: string;
  readonly erreur?: string;
  readonly children: (liens: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
  }) => ReactNode;
};

export function Champ({ libelle, aide, erreur, children }: ProprietesDuChamp) {
  const base = useId();
  const idChamp = `${base}-champ`;
  const idAide = aide === undefined ? undefined : `${base}-aide`;
  const idErreur = erreur === undefined ? undefined : `${base}-erreur`;
  const decrits = [idAide, idErreur].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={idChamp}
        className="text-14 font-weight-700 text-texte-fort"
      >
        {libelle}
      </label>
      {children({
        id: idChamp,
        'aria-describedby': decrits,
        'aria-invalid': erreur === undefined ? undefined : true,
      })}
      {aide !== undefined && (
        <p id={idAide} className="text-13 text-texte-faible">
          {aide}
        </p>
      )}
      {erreur !== undefined && (
        // `role="alert"` : une erreur de saisie qui n'est pas annoncée n'existe que
        // pour qui la voit.
        <p
          id={idErreur}
          role="alert"
          className="text-13 font-weight-700 text-erreur-texte"
        >
          {erreur}
        </p>
      )}
    </div>
  );
}

type ProprietesDeSaisie = InputHTMLAttributes<HTMLInputElement> & {
  readonly libelle: string;
  readonly aide?: string;
  readonly erreur?: string;
};

/** Un `<input>` natif, habillé. Aucune bibliothèque de champ n'est ajoutée. */
export function Saisie({ libelle, aide, erreur, ...reste }: ProprietesDeSaisie) {
  return (
    <Champ libelle={libelle} aide={aide} erreur={erreur}>
      {(liens) => (
        <input
          {...liens}
          {...reste}
          className={[
            'min-h-cible w-full rounded-2 bg-fond-surface px-3 py-2',
            'font-texte text-16 text-texte-fort',
            'border',
            erreur === undefined ? 'border-bord-champ' : 'border-erreur-bord',
          ].join(' ')}
        />
      )}
    </Champ>
  );
}
